import './PaymentPage.scss';

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { boolean } from 'yup';

import BackLink from '@components/BackLink';
import BottomBar from '@components/BottomBar';
import {
  Form,
  FormOptionsField,
  FormOptionValue,
  FormSection,
  FormSubmitButton,
  FormTextField,
  FormToggleField,
} from '@components/Form';
import { FormOptionsFieldContext } from '@components/Form/FormOptionsField';
import { useFormField } from '@components/Form/useFormField';
import { useInjection } from '@components/Injection';
import Layout from '@components/Layout';
import { Wizard, WizardNextButton, WizardPreviousButton, WizardStep, WizardSteps } from '@components/Wizard';
import { RoutePath } from '@constants/route-path';
import { IBraceShipping } from '@models/brace-order';
import { IPatient } from '@models/patient';
import { useAuth } from '@src/contexts/authContext';
import { IPatientsService, PATIENTS_SERVICE } from '@src/services';

const initPatient: IPatient = {
  clinicId: 0,
  firstName: '',
  lastName: '',
  dateOfBirth: new Date('1900-01-01'),
  email: '',
  phoneNumber: '',
  hasNoHealthInsurance: true,
};

const initialValues: IBraceShipping = {
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  province: '',
  country: '',
  postalCode: '',
  shippingOption: '',
  paymentOption: '',
  useShippingAsBilling: false,
};

function PaymentPage() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const authContext = useAuth();

  const patientsService = useInjection<IPatientsService>(PATIENTS_SERVICE);

  const [patient, setPatient] = useState<IPatient>(initPatient);

  useEffect(() => {
    const fetchPatient = async () => {
      const patientValue = await patientsService.getById(patientId, authContext.userObject.clinicId);
      setPatient(patientValue);
    };
    fetchPatient();
  }, [patientId]);

  const orderBrace = (shippingInfo: IBraceShipping) => {
    console.log(shippingInfo);
    if (shippingInfo.paymentOption === 'stripe') {
      window.location.href = 'https://buy.stripe.com/test_dR603d2mB0g36Iw144';
    } else {
      navigate(patientPath);
    }
  };

  const patientPath = generatePath(RoutePath.SinglePatientPage, { patientId });
  return (
    <Layout noFooter>
      <BackLink to={patientPath}>
        {patient.firstName} {patient.lastName}
      </BackLink>
      <h1 className="modTitle modTitle--h1">{patient.firstName}&apos;s brace order</h1>
      <Form initialValues={initialValues} onSubmit={orderBrace}>
        {({ values }) => (
          <>
            <FormSection title="Shipping informations">
              <FormTextField name="firstName" label="First name:" />
              <FormTextField name="lastName" label="Last name:" />
              <FormTextField name="addressLine1" label="Address line 1:" />
              <FormTextField name="addressLine2" label="Address line 2:" />
              <FormTextField name="city" label="City:" />
              <FormTextField name="province" label="State/Province:" />
              <FormTextField name="country" label="Country:" />
              <FormTextField name="postalCode" label="Postal code:" />
              <FormOptionsField name="shippingOption" label="Shipping method:" size={100}>
                <FormOptionValue value="free">Free (20-25 days)</FormOptionValue>
                <FormOptionValue value="expedited">Expedited (10-15 days)</FormOptionValue>
                <FormOptionValue value="express">Express (5-10 days)</FormOptionValue>
              </FormOptionsField>
              <div className="modPaymentForm__title">Payment informations</div>
              <FormOptionsField
                name="paymentOption"
                label="Which payment method would you like to use?"
                size={100}>
                <FormOptionValue value="stripe">Pay with Credit Card</FormOptionValue>
                <FormOptionValue value="invoice">Request Invoice</FormOptionValue>
              </FormOptionsField>
              {values.paymentOption === 'invoice' && (
                <FormToggleField
                  name="useShippingAsBilling"
                  label="Use shipping address as billing address?"
                  size={100}
                />
              )}
              {!values.useShippingAsBilling && values.paymentOption === 'invoice' && (
                <>
                  <FormTextField name="firstName2" label="Name:" />
                  <FormTextField name="lastName2" label="Company:" />
                  <FormTextField name="addressLine12" label="Address line 1:" />
                  <FormTextField name="addressLine22" label="Address line 2:" />
                  <FormTextField name="city2" label="City:" />
                  <FormTextField name="province2" label="State/Province:" />
                  <FormTextField name="country2" label="Country:" />
                  <FormTextField name="postalCode2" label="Postal code:" />
                </>
              )}
            </FormSection>
            <BottomBar>
              <FormSubmitButton>
                <i className="modIcon modIcon--check" />
                {values.paymentOption === 'stripe' ? 'Go to payment' : 'Confirm'}
              </FormSubmitButton>
            </BottomBar>
          </>
        )}
      </Form>
    </Layout>
  );
}

export default PaymentPage;
